// ไฟล์: frontend/src/App.js
import React, { useState, useEffect } from "react";
import {
  BrowserRouter as Router,
  Routes,
  Route,
  Navigate,
} from "react-router-dom";
import axios from "axios";



// Layout Components
import Header from "./components/layout/Header";
import Footer from "./components/layout/Footer";

// Page Components
import HomePage from "./pages/HomePage";
import LoginPage from "./pages/LoginPage";
import RegisterPage from "./pages/RegisterPage";
import DashboardPage from "./pages/DashboardPage";
import UserDashboardPage from "./pages/UserDashboardPage";
import ResourcesPage from "./pages/ResourcesPage";
import ResourceDetailPage from "./pages/ResourceDetailPage";
import UploadResourcePage from "./pages/UploadResourcePage";
import InnovationsPage from "./pages/InnovationsPage";
import InnovationDetailPage from "./pages/InnovationDetailPage";
import UploadInnovationPage from "./pages/UploadInnovationPage";
import ArticlesPage from "./pages/ArticlesPage";
import ArticleDetailPage from "./pages/ArticleDetailPage";
import WriteArticlePage from "./pages/WriteArticlePage";
import NewsPage from "./pages/NewsPage";
import NewsDetailPage from "./pages/NewsDetailPage";
import CreateNewsPage from "./pages/CreateNewsPage";
import LibraryPage from "./pages/LibraryPage";
import SearchPage from "./pages/SearchPage";
import TeacherProfilePage from "./pages/TeacherProfilePage";
import UserProfilePage from "./pages/UserProfilePage";
import AdminUsersPage from "./pages/admin/AdminUsersPage";
import AdminCategoriesPage from "./pages/admin/AdminCategoriesPage";
import AdminSubjectsPage from "./pages/admin/AdminSubjectsPage";
import AdminGradeLevelsPage from "./pages/admin/AdminGradeLevelsPage";
import AdminResourcesPage from "./pages/admin/AdminResourcesPage";
import NotFoundPage from "./pages/NotFoundPage";
import AdminInnovationsPage from "./pages/admin/AdminInnovationsPage";
import AdminArticlesPage from "./pages/admin/AdminArticlesPage";

// CSS
import "./App.css";

// API Base URL
// const API_URL = process.env.REACT_APP_API_URL || "http://localhost:5000/api";
const API_URL = "https://teachershare.backend.kruwirat.com/api";

// ตั้งค่า Default Base URL สำหรับ Axios
axios.defaults.baseURL = API_URL;

function App() {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [systemInfo, setSystemInfo] = useState({
    name: process.env.REACT_APP_SYSTEM_NAME || "TeacherShare",
    organization:
      process.env.REACT_APP_ORGANIZATION_NAME ||
      "เว็บไซต์แห่งการแบ่งปันเพื่อเพื่อนครู",
    logo:
      process.env.REACT_APP_LOGO_URL ||
      "https://www.i-pic.info/i/1wLI1090028.png",
  });

  // ดึงข้อมูลผู้ใช้จาก token
  useEffect(() => {
    const token = localStorage.getItem("token");

    const fetchCurrentUser = async () => {
      if (token) {
        try {
          // ตั้งค่า header สำหรับ token
          axios.defaults.headers.common["Authorization"] = `Bearer ${token}`;

          const response = await axios.get("/auth/me");
          setUser(response.data.user);
        } catch (error) {
          console.error("Error fetching user:", error);
          localStorage.removeItem("token");
          delete axios.defaults.headers.common["Authorization"];
        }
      }

      setLoading(false);
    };

    fetchCurrentUser();
  }, []);

  // ฟังก์ชันสำหรับเข้าสู่ระบบ
  const handleLogin = (userData, token) => {
    setUser(userData);
    localStorage.setItem("token", token);
    axios.defaults.headers.common["Authorization"] = `Bearer ${token}`;
  };

  // ฟังก์ชันสำหรับออกจากระบบ
  const handleLogout = () => {
    setUser(null);
    localStorage.removeItem("token");
    delete axios.defaults.headers.common["Authorization"];
  };

  // ฟังก์ชันสำหรับอัพเดทข้อมูลผู้ใช้
  const updateUser = (updatedUserData) => {
    setUser((prevUser) => ({ ...prevUser, ...updatedUserData }));
  };

  // Protected Route Component
  const ProtectedRoute = ({ children, admin = false }) => {
    if (loading) {
      return <div className="loading-spinner">กำลังโหลด...</div>;
    }

    if (!user) {
      return <Navigate to="/login" />;
    }

    if (admin && user.role !== "admin") {
      return <Navigate to="/" />;
    }

    return children;
  };

  if (loading) {
    return <div className="loading-spinner"> </div>;
  }

  return (
    <Router>
      <div className="app-container">
        <Header user={user} onLogout={handleLogout} systemInfo={systemInfo} />

        <main className="main-content">
          <Routes>
            {/* Public Routes */}
            <Route path="/" element={<HomePage user={user} />} />
            <Route
              path="/login"
              element={
                user ? <Navigate to="/" /> : <LoginPage onLogin={handleLogin} />
              }
            />
            <Route
              path="/register"
              element={user ? <Navigate to="/" /> : <RegisterPage />}
            />
            <Route path="/resources" element={<ResourcesPage />} />
            <Route
              path="/resources/:id"
              element={<ResourceDetailPage user={user} />}
            />
            <Route path="/innovations" element={<InnovationsPage />} />
            <Route
              path="/innovations/:id"
              element={<InnovationDetailPage user={user} />}
            />
            <Route path="/articles" element={<ArticlesPage />} />
            <Route
              path="/articles/:id"
              element={<ArticleDetailPage user={user} />}
            />
            <Route path="/news" element={<NewsPage />} />
            <Route path="/news/:id" element={<NewsDetailPage user={user} />} />
            <Route path="/search" element={<SearchPage />} />
            <Route
              path="/teachers/:id"
              element={<TeacherProfilePage user={user} />}
            />

            {/* Protected Routes */}
            <Route
              path="/dashboard"
              element={
                <ProtectedRoute>
                  <UserDashboardPage user={user} />
                </ProtectedRoute>
              }
            />
            <Route
              path="/upload-resource"
              element={
                <ProtectedRoute>
                  <UploadResourcePage user={user} />
                </ProtectedRoute>
              }
            />
            <Route
              path="/upload-innovation"
              element={
                <ProtectedRoute>
                  <UploadInnovationPage user={user} />
                </ProtectedRoute>
              }
            />
            <Route
              path="/write-article"
              element={
                <ProtectedRoute>
                  <WriteArticlePage user={user} />
                </ProtectedRoute>
              }
            />
            <Route
              path="/library"
              element={
                <ProtectedRoute>
                  <LibraryPage user={user} />
                </ProtectedRoute>
              }
            />
            <Route
              path="/profile"
              element={
                <ProtectedRoute>
                  <UserProfilePage user={user} updateUser={updateUser} />
                </ProtectedRoute>
              }
            />

            {/* Admin Routes */}
            <Route
              path="/admin/dashboard"
              element={
                <ProtectedRoute admin={true}>
                  <DashboardPage user={user} />
                </ProtectedRoute>
              }
            />
            <Route
              path="/admin/users"
              element={
                <ProtectedRoute admin={true}>
                  <AdminUsersPage user={user} />
                </ProtectedRoute>
              }
            />
            <Route
              path="/admin/categories"
              element={
                <ProtectedRoute admin={true}>
                  <AdminCategoriesPage user={user} />
                </ProtectedRoute>
              }
            />
            <Route
              path="/admin/subjects"
              element={
                <ProtectedRoute admin={true}>
                  <AdminSubjectsPage user={user} />
                </ProtectedRoute>
              }
            />
            <Route
              path="/admin/grade-levels"
              element={
                <ProtectedRoute admin={true}>
                  <AdminGradeLevelsPage user={user} />
                </ProtectedRoute>
              }
            />
            <Route
              path="/admin/resources"
              element={
                <ProtectedRoute admin={true}>
                  <AdminResourcesPage user={user} />
                </ProtectedRoute>
              }
            />

            <Route
              path="/admin/innovations"
              element={
                <ProtectedRoute admin={true}>
                  <AdminInnovationsPage user={user} />
                </ProtectedRoute>
              }
            />

            <Route
              path="/admin/articles"
              element={
                <ProtectedRoute admin={true}>
                  <AdminArticlesPage user={user} />
                </ProtectedRoute>
              }
            />

            <Route
              path="/admin/create-news"
              element={
                <ProtectedRoute admin={true}>
                  <CreateNewsPage user={user} />
                </ProtectedRoute>
              }
            />

            {/* 404 Route */}
            <Route path="*" element={<NotFoundPage />} />
          </Routes>
        </main>

        <Footer systemInfo={systemInfo} />
      </div>
    </Router>
  );
}

export default App;
